<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="flex items-center justify-between border-b border-white/10 px-5 py-4">
      <div><h2 class="text-sm font-bold">Live chat</h2><p class="mt-0.5 text-xs text-zinc-500">Everyone in this Webinar</p></div>
      <span class="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider" :class="websocket.isConnected ? 'bg-emerald-400/10 text-emerald-300' : 'bg-amber-400/10 text-amber-300'">{{ websocket.isConnected ? 'Connected' : 'Reconnecting' }}</span>
    </div>

    <div v-if="chatError" class="border-b border-amber-400/10 bg-amber-400/5 px-5 py-3 text-xs leading-5 text-amber-200">{{ chatError }}</div>

    <div class="relative min-h-0 flex-1 overflow-hidden">
      <div v-show="showTopFade" aria-hidden="true" class="pointer-events-none absolute inset-x-0 top-0 z-20 h-16 bg-gradient-to-b from-[#15171c] via-[#15171c]/90 to-transparent transition-opacity duration-200" />
      <div ref="chatContainer" class="app-scroll h-full overflow-y-auto px-4 py-4" @scroll="updateTopFade">
        <div v-if="!messages.length" class="flex h-full flex-col items-center justify-center px-4 text-center">
          <div class="flex h-14 w-14 items-center justify-center rounded-full bg-blue-500/10 ring-1 ring-blue-400/15"><ChatBubbleLeftRightIcon class="h-7 w-7 text-blue-300" /></div>
          <p class="mt-4 text-sm font-semibold text-zinc-200">Chat will appear here</p>
          <p class="mt-2 max-w-[260px] text-xs leading-5 text-zinc-500">Messages from members and the host are scoped to this Webinar.</p>
        </div>

        <TransitionGroup v-else name="studio-chat-message" tag="div" class="space-y-4">
          <article v-for="message in messages" :key="message.messageId" class="group flex items-start pl-1">
            <img :src="messageAvatar(message)" :alt="`${message.senderName} avatar`" class="relative z-10 mt-3 h-9 w-9 shrink-0 rounded-full bg-zinc-800 object-cover ring-2 ring-[#15171c]" @error="useDefaultAvatar" />
            <div class="-ml-3 min-w-0 flex-1 rounded-2xl rounded-tl-md border border-white/5 bg-white/[0.04] py-3 pl-6 pr-3.5">
              <div class="flex items-center gap-2">
                <span class="min-w-0 truncate text-xs font-bold text-zinc-200">{{ message.senderName }}</span>
                <span v-if="message.senderType !== 'member'" class="rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blue-300">{{ message.senderType === 'moderator' ? 'Moderator' : 'Host' }}</span>
                <time class="text-[10px] text-zinc-600">{{ formatMessageTime(message.createdAt) }}</time>
                <button
                  v-if="message.senderType === 'member'"
                  type="button"
                  class="ml-auto rounded-lg p-1.5 text-zinc-600 opacity-0 transition hover:bg-red-500/10 hover:text-red-300 group-hover:opacity-100 focus:opacity-100 disabled:cursor-wait disabled:opacity-50"
                  :disabled="deletingMessageId === message.messageId"
                  :aria-label="`Delete message from ${message.senderName}`"
                  title="Delete member message"
                  @click="deleteMessage(message)"
                ><TrashIcon class="h-4 w-4" /></button>
              </div>
              <p class="mt-1 whitespace-pre-wrap break-words text-zinc-300" :class="isLargeEmojiMessage(message.message) ? 'text-4xl leading-tight' : 'text-sm leading-5'"><template v-for="(part, index) in messageParts(message)" :key="`${message.messageId}-${index}`"><a v-if="part.link" :href="part.text" target="_blank" rel="noopener noreferrer" class="font-medium text-blue-300 underline decoration-blue-400/50 underline-offset-2 hover:text-blue-200">{{ part.text }}</a><span v-else>{{ part.text }}</span></template></p>
            </div>
          </article>
        </TransitionGroup>
      </div>
    </div>

    <div class="border-t border-white/10 p-4">
      <div class="rounded-xl border border-white/10 bg-black/20 px-3 py-2 focus-within:border-blue-400/40 focus-within:ring-2 focus-within:ring-blue-400/10">
        <textarea ref="composerTextarea" v-model="newMessage" rows="2" maxlength="500" class="app-scroll block max-h-32 min-h-[46px] w-full resize-none overflow-y-hidden border-0 bg-transparent p-0 text-sm leading-5 text-zinc-200 outline-none placeholder:text-zinc-600 focus:ring-0" placeholder="Reply as the host…" aria-label="Creator Webinar chat message" @keydown="handleComposerKeydown" />
        <div class="mt-2 flex items-center justify-between gap-2">
          <span class="text-[10px] text-zinc-600">Enter sends · Shift/Ctrl + Enter adds a line</span>
          <div class="flex items-center gap-2"><span class="text-[10px] text-zinc-600">{{ charCount }}/500</span><button type="button" class="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500 text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-500" :disabled="!canSend" aria-label="Send Webinar chat message" @click="sendMessage"><PaperAirplaneIcon class="h-4 w-4" /></button></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ChatBubbleLeftRightIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { PaperAirplaneIcon } from '@heroicons/vue/24/solid';
import defaultAvatar from '@/assets/media/images/default-avatar.png';
import { useWebsocketStore } from '@/stores/websocketStore';

const props = defineProps({
  eventId: { type: Number, required: true },
  featureInstanceId: { type: Number, required: true },
});

const websocket = useWebsocketStore();
const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/$/, '');
const messages = ref([]);
const newMessage = ref('');
const chatError = ref('');
const chatContainer = ref(null);
const composerTextarea = ref(null);
const showTopFade = ref(false);
const deletingMessageId = ref(0);
const charCount = computed(() => Array.from(newMessage.value).length);
const canSend = computed(() => websocket.isConnected && charCount.value > 0 && charCount.value <= 500 && newMessage.value.trim().length > 0);

let unsubscribeSnapshot = null;
let unsubscribeMessage = null;
let unsubscribeDeleted = null;
let unsubscribeError = null;
let stopConnectionWatch = null;
let snapshotRetryTimer = null;
let historyLoaded = false;
const chatSubscriptionId = typeof globalThis.crypto?.randomUUID === 'function'
  ? globalThis.crypto.randomUUID()
  : `webinar-studio-chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function isThisChat(payload = {}) {
  return Number(payload.eventId) === props.eventId
    && (!payload.featureInstanceId || Number(payload.featureInstanceId) === props.featureInstanceId);
}

function sendChatEvent(event, extra = {}) {
  if (!websocket.isConnected || !props.eventId || !props.featureInstanceId) return;
  websocket.send({
    type: 'creator',
    event,
    payload: {
      eventId: props.eventId,
      featureInstanceId: props.featureInstanceId,
      subscriptionId: chatSubscriptionId,
      ...extra,
    },
    ts: Date.now(),
  });
}

function subscribeToChat() {
  if (!websocket.isConnected || !props.eventId || !props.featureInstanceId) return;
  chatError.value = '';
  historyLoaded = false;
  sendChatEvent('webinar:chatSubscribe');
  window.clearTimeout(snapshotRetryTimer);
  snapshotRetryTimer = window.setTimeout(() => {
    if (websocket.isConnected && !historyLoaded) subscribeToChat();
  }, 1500);
}

function normalizeIncoming(message = {}) {
  return {
    messageId: Number(message.messageId || 0),
    eventId: Number(message.eventId || 0),
    featureInstanceId: Number(message.featureInstanceId || 0),
    senderType: String(message.senderType || 'member'),
    senderId: Number(message.senderId || 0),
    senderName: String(message.senderName || 'Member'),
    senderAvatarUrl: String(message.senderAvatarUrl || ''),
    senderAvatarMediaFileId: String(message.senderAvatarMediaFileId || ''),
    message: String(message.message || ''),
    createdAt: message.createdAt || new Date().toISOString(),
  };
}

function handleSnapshot(payload = {}) {
  if (!isThisChat(payload)) return;
  historyLoaded = true;
  window.clearTimeout(snapshotRetryTimer);
  messages.value = (Array.isArray(payload.messages) ? payload.messages : []).map(normalizeIncoming).filter((message) => message.messageId > 0);
  chatError.value = '';
}

function handleNewMessage(payload = {}) {
  if (!isThisChat(payload)) return;
  const message = normalizeIncoming(payload);
  if (!message.messageId || messages.value.some((existing) => existing.messageId === message.messageId)) return;
  messages.value.push(message);
}

function handleDeleted(payload = {}) {
  if (!isThisChat(payload)) return;
  const messageId = Number(payload.messageId || 0);
  messages.value = messages.value.filter((message) => message.messageId !== messageId);
  if (deletingMessageId.value === messageId) deletingMessageId.value = 0;
}

function handleError(payload = {}) {
  if (Number(payload.eventId || 0) !== props.eventId) return;
  chatError.value = String(payload.message || 'Webinar chat is temporarily unavailable');
  deletingMessageId.value = 0;
}

function sendMessage() {
  if (!canSend.value) return;
  const message = Array.from(newMessage.value).slice(0, 500).join('').trim();
  if (!message) return;
  sendChatEvent('webinar:chatSend', { message });
  newMessage.value = '';
}

function deleteMessage(message) {
  if (!message?.messageId || deletingMessageId.value) return;
  deletingMessageId.value = message.messageId;
  sendChatEvent('webinar:chatDelete', { messageId: message.messageId });
}

function handleComposerKeydown(event) {
  if (event.key !== 'Enter') return;
  if (event.shiftKey || event.ctrlKey || event.metaKey) return;
  event.preventDefault();
  sendMessage();
}

function useDefaultAvatar(event) {
  if (event?.target && event.target.src !== defaultAvatar) event.target.src = defaultAvatar;
}

function messageAvatar(message = {}) {
  const mediaFileId = String(message.senderAvatarMediaFileId || '').trim();
  if (['creator', 'moderator'].includes(message.senderType) && mediaFileId) {
    return `${apiProxyUrl}/api/image/${encodeURIComponent(mediaFileId)}`;
  }
  return message.senderAvatarUrl || defaultAvatar;
}

function formatMessageTime(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function messageParts(message) {
  if (!['creator', 'moderator'].includes(message.senderType)) return [{ text: message.message, link: false }];
  return message.message.split(/(https?:\/\/[^\s]+)/gi).filter(Boolean).map((part) => ({ text: part, link: /^https?:\/\//i.test(part) }));
}

function isLargeEmojiMessage(value) {
  const text = String(value || '').trim();
  if (!text) return false;
  const segments = typeof Intl !== 'undefined' && Intl.Segmenter
    ? Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text), (entry) => entry.segment).filter((part) => !/^\s+$/u.test(part))
    : Array.from(text).filter((part) => !/^\s+$/u.test(part));
  if (!segments.length || segments.length > 3) return false;
  return segments.every((segment) => {
    if (!/(?:\p{Extended_Pictographic}|\p{Regional_Indicator})/u.test(segment)) return false;
    const remainder = segment
      .replace(/\p{Extended_Pictographic}/gu, '')
      .replace(/\p{Regional_Indicator}/gu, '')
      .replace(/\p{Emoji_Modifier}/gu, '')
      .replace(/\u200D/gu, '')
      .replace(/\uFE0F/gu, '');
    return remainder === '';
  });
}

function updateTopFade() {
  showTopFade.value = Boolean(chatContainer.value && chatContainer.value.scrollTop > 4);
}

function resizeComposer() {
  nextTick(() => {
    const composer = composerTextarea.value;
    if (!composer) return;
    const chat = chatContainer.value;
    const keepAtBottom = !chat || chat.scrollHeight - chat.scrollTop - chat.clientHeight < 32;
    composer.style.height = 'auto';
    composer.style.height = `${Math.min(composer.scrollHeight, 128)}px`;
    composer.style.overflowY = composer.scrollHeight > 128 ? 'auto' : 'hidden';
    if (chat && keepAtBottom) chat.scrollTop = chat.scrollHeight;
    updateTopFade();
  });
}

watch(newMessage, resizeComposer);

watch(
  () => [props.eventId, props.featureInstanceId],
  ([nextEventId, nextFeatureInstanceId], [previousEventId, previousFeatureInstanceId]) => {
    window.clearTimeout(snapshotRetryTimer);
    historyLoaded = false;
    messages.value = [];
    chatError.value = '';

    if (websocket.isConnected && previousEventId && previousFeatureInstanceId) {
      websocket.send({
        type: 'creator',
        event: 'webinar:chatUnsubscribe',
        payload: {
          eventId: previousEventId,
          featureInstanceId: previousFeatureInstanceId,
          subscriptionId: chatSubscriptionId,
        },
        ts: Date.now(),
      });
    }
    if (nextEventId && nextFeatureInstanceId) subscribeToChat();
  }
);

watch(() => messages.value.length, () => nextTick(() => {
  if (chatContainer.value) chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  updateTopFade();
}));

onMounted(() => {
  resizeComposer();
  unsubscribeSnapshot = websocket.subscribe('webinar:chatSnapshot', handleSnapshot);
  unsubscribeMessage = websocket.subscribe('webinar:chatMessage', handleNewMessage);
  unsubscribeDeleted = websocket.subscribe('webinar:chatDeleted', handleDeleted);
  unsubscribeError = websocket.subscribe('webinar:chatError', handleError);
  stopConnectionWatch = watch(() => websocket.isConnected, (connected) => {
    if (connected) subscribeToChat();
  });
  websocket.connect();
  if (websocket.isConnected) subscribeToChat();
});

onBeforeUnmount(() => {
  window.clearTimeout(snapshotRetryTimer);
  sendChatEvent('webinar:chatUnsubscribe');
  if (unsubscribeSnapshot) unsubscribeSnapshot();
  if (unsubscribeMessage) unsubscribeMessage();
  if (unsubscribeDeleted) unsubscribeDeleted();
  if (unsubscribeError) unsubscribeError();
  if (stopConnectionWatch) stopConnectionWatch();
});
</script>

<style scoped>
.studio-chat-message-enter-active,
.studio-chat-message-leave-active,
.studio-chat-message-move { transition: opacity 180ms ease, transform 180ms ease; }
.studio-chat-message-enter-from,
.studio-chat-message-leave-to { opacity: 0; transform: translateY(6px); }

.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
}

.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent;
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
